import { useEffect, useState } from "react";
import {Link, withRouter} from "react-router-dom";
import { defineMessages, injectIntl } from "react-intl";
import PropTypes from 'prop-types';
import { Col, Form, Button } from 'react-bootstrap';
import AlertDialog from "./AlertDialog";

const messages = defineMessages({
    enterCode: {
        id: "Syötä koodi",
        defaultMessage: "Syötä koodi"
    },
    instructionsCode: {
        id: "Vahvistuskoodi lähetettiin osoitteeseen",
        defaultMessage: "Vahvistuskoodi lähetettiin osoitteeseen"
    },
    changeEmailAddress: {
        id: "Vaihda",
        defaultMessage: "Vaihda"
    },
    codeLabel: {
        id: "Koodi",
        defaultMessage: "Koodi"
    },
    codeValidationErrorIncorrect: {
        id: "Virheellinen koodi",
        defaultMessage: "Virheellinen koodi"
    },
    codeValidationErrorNotNumeric: {
        id: "Vain numerot ovat sallittuja",
        defaultMessage: "Vain numerot ovat sallittuja"
    },
    resendCode: {
        id: "Lähetä koodi uudelleen",
        defaultMessage: "Lähetä koodi uudelleen"
    }
});

const SignInCode = (props) => {
    const lng = props.intl.formatMessage;
    const inputMaxLength = props.codeLength; // must always be provided (see PropTypes)
    const emailSentTo = props.email;
    const [codeInput, setCodeInput] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        // toggle error messages from submit
        if (props.error === 'incorrectCode') {
            setError(lng(messages.codeValidationErrorIncorrect));
        } else {
            setError(props.error);
        }

    }, [props.error]);

    /**
     * Handle key down events in the form.
     * 
     * Prevent submission on Enter key press, because form should be submitted
     * only when max length of input is reached. Also show errors 
     * if input is not at max length when enter is pressed.
     * 
     * @param {*} event 
     */
    const handleKeyDown = (event) => {
        if (event.key === "Enter") {
            event.preventDefault();
            if (codeInput.length < inputMaxLength) {
                setError(lng(messages.codeValidationErrorIncorrect));
            }
        }
    };

    /**
     * Handle confirmation code input.
     * 
     * By default, only digts are allowed, but passed props determine the allowed max length of the input.
     * If the input reaches the max length, it calls the onSubmit function passed from the parent.
     * 
     * @param {*} event 
     */
    const handleCodeInput = (event) => {
        // allow only digits
        const value = event.target.value.replace(/\D/g, "");
        setCodeInput(value);

        if (isNaN(Number(event.target.value))) {
            // show error if user tries to enter non-numeric characters
            setError(lng(messages.codeValidationErrorNotNumeric));
        } else {
            setError("");
        }

        if (value.length === inputMaxLength) {
            // remove errors and submit when ok
            setError("");
            props.onSubmit(value, emailSentTo);
        }
    };

    return (
        <>
            <span>
                <h2>{lng(messages.enterCode)}</h2>
                <span className="d-flex align-items-center">
                    <p className="m-0 ">{lng(messages.instructionsCode)} {emailSentTo}.</p>
                    <Link to="/user/signin" className="ms-2">
                        {lng(messages.changeEmailAddress)}
                    </Link>
                </span>
            </span>
            <Form
                id={`confirmation-code-form-${props.confirmAction}`}
                aria-label={lng(messages.enterCode)}
                className="w-100"
                autoComplete="off"
                noValidate
                onKeyDown={handleKeyDown}
            >
                {props.alert && (
                    <AlertDialog
                        type={props.alert.type ?? 'danger'}
                        message={props.alert.message}
                    />
                )}
                <Form.Floating className="mb-2 pb-2">
                    <Form.Control
                        readOnly={props.loading}
                        id={`confirmation-code-${props.confirmAction}`}
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        value={codeInput}
                        maxLength={inputMaxLength}
                        placeholder={lng(messages.codeLabel)}
                        autoComplete="off"
                        onChange={handleCodeInput}
                        isInvalid={!!error}
                        required
                    />
                    <Form.Label htmlFor={`confirmation-code-${props.confirmAction}`}>
                        {lng(messages.codeLabel)}
                    </Form.Label>
                    <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
                </Form.Floating>
            </Form>

            <Col xs={12} className="d-inline-flex justify-content-between">
                <Button
                    variant="tertiary"
                    className="ps-0"
                    onClick={props.onResendCode}
                    disabled={props.loading}
                >
                    {lng(messages.resendCode)}
                </Button>
            </Col>
        </>
    )
};

SignInCode.propTypes = {
    'intl': PropTypes.object.isRequired,
    'confirmAction': PropTypes.string,
    'codeLength': PropTypes.number.isRequired,
    'onSubmit': PropTypes.func.isRequired,
    'onResendCode': PropTypes.func,
    'loading': PropTypes.bool,
    'error': PropTypes.string,
    'email': PropTypes.string.isRequired,
    'alert': PropTypes.object
};

export default withRouter(injectIntl(SignInCode));
